import {
  FloatingArrow,
  arrow,
  autoUpdate,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
  shift,
  flip,
} from '@floating-ui/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';

import Theme from 'styles/theme';

export const PLACEMENT = {
  TOP: 'top',
  BOTTOM: 'bottom',
};

const ARROW_HEIGHT = Theme.get('spacing') * 2;
const ARROW_WIDTH = Theme.get('spacing') * 4;

function Tooltip({
  options = {
    placement: PLACEMENT.TOP,
    positionFixed: true,
  },
  children = null,
  content = null,
  className = '',
  onView = () => {},
  open = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context, isPositioned, middlewareData } = useFloating({
    middleware: [
      arrow({ element: arrowRef }),
      offset(ARROW_HEIGHT),
      flip({
        padding: {
          top: 100,
          bottom: 100,
          left: 0,
          right: 0,
        },
      }),
      shift(),
    ],
    open: isOpen,
    onOpenChange(isOpened, event) {
      setIsOpen(isOpened);
      if (isOpened && isOpen !== isOpened) {
        onView(event);
      }
    },
    placement: options.placement,
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    if (isPositioned || open) {
      onView();
    }
  }, [isPositioned, onView, open]);

  const arrowX = middlewareData.arrow?.x ?? 0;
  const arrowY = middlewareData.arrow?.y ?? 0;
  const transformX = arrowX + ARROW_WIDTH / 2;
  const transformY = arrowY + ARROW_HEIGHT;

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      transform: 'scale(0)',
    },
    common: ({ side }) => ({
      transformOrigin: {
        top: `${transformX}px calc(100% + ${ARROW_HEIGHT}px)`,
        bottom: `${transformX}px ${-ARROW_HEIGHT}px`,
        left: `calc(100% + ${ARROW_HEIGHT}px) ${transformY}px`,
        right: `${-ARROW_HEIGHT}px ${transformY}px`,
      }[side],
    }),
  });

  const click = useClick(context);
  const hover = useHover(context);
  const role = useRole(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, hover, role]);

  return (
    <div className={classNames('Tooltip', className)}>
      <button className="Tooltip__anchor" ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </button>

      {isMounted && (
        <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <div className={styles}>
            <div className="Tooltip__content">{content}</div>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={Theme.get('colors-white')}
              width={ARROW_WIDTH}
              height={ARROW_HEIGHT}
            />
          </div>
        </div>
      )}

      <style jsx global>{`
        .Tooltip {
          position: relative;
          z-index: 999;
        }

        .Tooltip__content {
          box-shadow: ${Theme.get('spacing') * 0.5}px ${Theme.get('spacing') * 0.5}px
            ${Theme.get('spacing') * 2}px ${Theme.get('spacing') / 3}px
            ${Theme.get('colors-shadow')};
          width: max-content;
          max-width: 250px;
          padding: ${Theme.get('spacing') * 3}px;
          line-height: 20px;
          font-size: ${Theme.get('font-size-small-px')};
          font-weight: ${Theme.get('font-weight-semi-bold')};
          background-color: ${Theme.get('colors-white')};
          border-radius: ${Theme.get('border-radius-px')};
          order: 1;
        }

        .Tooltip__tooltip {
          position: fixed;
          pointer-events: none;
        }

        .Tooltip__anchor {
          display: block;
          appearance: none;
          cursor: default;
          background-color: transparent;
          border: none;
          margin: 0;
          padding: 0;
          outline: none;
        }

        .Tooltip__anchor.focused {
          outline: 2px solid ${Theme.get('colors-black')};
        }
      `}</style>
    </div>
  );
}

Tooltip.propTypes = {
  options: PropTypes.shape({
    placement: PropTypes.oneOf(Object.values(PLACEMENT)),
  }),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  content: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  className: PropTypes.string,
  onView: PropTypes.func,
  open: PropTypes.bool,
};

Tooltip.defaultProps = {
  options: {
    placement: PLACEMENT.TOP,
    positionFixed: true,
  },
  children: null,
  content: null,
  className: '',
  onView: () => {},
  open: false,
};

export default Tooltip;
